import moment from 'moment'

// How long a client may keep running an outdated bundle before we interrupt
// them about it. Most clients never reach this: a route change reloads them
// onto the current build (App.vue's 'navigation' handler), so this only covers
// someone parked on a single page.
export const UPDATE_PROMPT_AFTER_MINUTES = 10

// `staleSince` is latched by SET_APP_VERSION at the moment the server's version
// first stopped matching the one this page loaded with — it is deliberately not
// refreshed by later responses, because the heartbeat runs every 30s and a
// moving timestamp can never age past the grace period. That is precisely how
// this prompt sat dead in production (see ESS-POS-WEB-69V).
export function shouldPromptForUpdate(staleSince, now = moment()) {
    if (! staleSince) {
        return false
    }
    return staleSince.isBefore(moment(now).subtract(UPDATE_PROMPT_AFTER_MINUTES, 'minutes'))
}

// Shortest gap between two version-triggered reloads of the same tab. One
// reload is all a normal deploy needs; a second one this soon means the
// mismatch survived the reload, which no amount of reloading will fix.
export const RELOAD_COOLDOWN_MINUTES = 10

// Routes whose path carries a single-use credential. Reloading one of these
// re-runs its mounted() hook, which exchanges the token a second time; the
// second exchange 404s (api/views.py:login_get_session filters on
// taken_over_on__isnull=True) and AutoLogin redirects to /login. So the user
// loses a login they had already completed, to pick up a new bundle.
//
// Kept as a list rather than one regex because the next one to appear will be
// added by someone who has not read this comment.
const ONE_TIME_TOKEN_ROUTES = [
    /^\/login\/auto\/[^/]+\/[^/]+/,
]

export function isOneTimeTokenRoute(path) {
    if (! path) {
        return false
    }
    const withoutQuery = path.split('?')[0]
    return ONE_TIME_TOKEN_ROUTES.some((pattern) => pattern.test(withoutQuery))
}

const RELOAD_MARK_KEY = 'gmps.versionReloadAt'

function defaultStorage() {
    return typeof window !== 'undefined' ? window.sessionStorage : null
}

// Decide whether to reload onto a new build, and record having done so.
//
// The reload itself clears the condition under normal circumstances: the store
// does not persist appVersionClient, so the fresh page adopts whatever version
// answers first and the mismatch is gone. The exception is a fleet serving two
// different shas at once — prod runs two web hosts behind one ALB, so a deploy
// that half-lands does exactly that, and then every response flips
// appVersionServer no matter how many times the tab reloads. A 401 alone can
// drive that without the user touching anything (api.js -> bus.sessionInvalid
// -> router.push -> the 'navigation' handler in App.vue), so the cooldown is
// what stands between a half-deploy and a tab reloading itself in a loop.
//
// The mark lives in sessionStorage because it has to outlive the reload it
// bounds and die with the tab. Where storage is unavailable (Safari private
// mode, locked-down webviews) we reload anyway — same behavior as before this
// guard existed.
export function claimVersionReload(clientVersion, serverVersion, now = moment(), storage = defaultStorage()) {
    if (! clientVersion || ! serverVersion || clientVersion == serverVersion) {
        return false
    }

    const cutoff = moment(now).subtract(RELOAD_COOLDOWN_MINUTES, 'minutes')
    try {
        const lastReload = storage && storage.getItem(RELOAD_MARK_KEY)
        if (lastReload && moment(lastReload).isAfter(cutoff)) {
            return false
        }
        storage && storage.setItem(RELOAD_MARK_KEY, moment(now).toISOString())
    }
    catch {
        // Storage unavailable — proceed without the cooldown.
    }

    return true
}
